import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Leaf, Zap, ShieldCheck } from "lucide-react";

const LEAF = "/brand/product/single-jar.jpg";
const MASCOT = "/brand/mascot.png";

const features = [
  { icon: Leaf, title: "Hand-Picked Strains", text: "A proprietary blend of hand-picked grabba leaf strains, the core of our flavor." },
  { icon: Zap, title: "Bold Character", text: "Full, unmistakable flavor that stands out from the first pull." },
  { icon: ShieldCheck, title: "Crushed & Consistent", text: "Finely crushed grabba with the same quality and flavor in every pack." },
];

export default function ProductShowcase() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="product" ref={ref} data-testid="product" className="relative py-24 sm:py-32 bg-surface overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-14 items-center">
        {/* Framed image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="relative border border-surface-border overflow-hidden aspect-[4/5]">
            <motion.img style={{ y }} src={LEAF} alt="Hot Packz Grabba jar of premium crushed loose leaf" className="w-full h-[120%] object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            <img
              src={MASCOT}
              alt="Hot Packz Grabba mascot"
              className="absolute bottom-0 right-4 w-40 sm:w-52 drop-shadow-[0_0_25px_rgba(255,42,0,0.4)]"
            />
            <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-gold bg-ink/70 px-3 py-1.5 border border-surface-border">
              The Grabba
            </span>
          </div>
        </motion.div>

        {/* Copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-blaze mb-4">The Product</p>
            <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.9] mb-6">
              A <span className="text-fire">blend</span>, not just a leaf
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-10 max-w-md">
              Hot Packz Grabba is crushed grabba from a proprietary blend of
              hand-picked leaf strains. It's the signature flavor that sets us
              apart.
            </p>
          </motion.div>

          <div className="space-y-px bg-surface-border border border-surface-border">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="group flex items-start gap-5 bg-ink p-6 hover:bg-surface transition-colors duration-300"
                >
                  <span className="shrink-0 border border-surface-border p-3 text-blaze group-hover:border-blaze group-hover:text-gold transition-colors duration-300">
                    <Icon size={22} />
                  </span>
                  <div>
                    <h3 className="font-heading uppercase text-lg tracking-tight mb-1">{f.title}</h3>
                    <p className="text-muted-foreground text-sm">{f.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
